import { Link } from "react-router-dom";
import {
  BadgeCheck,
  CalendarClock,
  FileSpreadsheet,
  Gauge,
  MessagesSquare,
  QrCode,
  ShieldCheck,
  Smartphone,
} from "lucide-react";
import { WhatsAppCta } from "../components/WhatsAppCta";
import { useSeo } from "../lib/useSeo";

const FEATURES = [
  {
    icon: FileSpreadsheet,
    title: "Importa a planilha, o resto e automatico",
    text: "Sobe um CSV (ou Excel exportado como CSV, virgula ou ponto-e-virgula, tanto faz), mapeia as colunas na tela e pronto - a campanha ja fica pronta pra disparar ou agendar.",
  },
  {
    icon: CalendarClock,
    title: "Agenda, pausa e retoma quando quiser",
    text: "Uma campanha agendada dispara sozinha na hora certa. Precisou pausar no meio? Os que ja foram enviados ficam enviados, o resto so continua quando voce mandar.",
  },
  {
    icon: Gauge,
    title: "Painel com numero de verdade",
    text: "Quantos foram enviados, entregues, lidos e quantos falharam - por campanha e por periodo, sem precisar abrir planilha nenhuma pra somar nada.",
  },
  {
    icon: ShieldCheck,
    title: "Intervalo configuravel no WhatsApp QR",
    text: "Disparo rapido demais e o motivo mais comum de numero restrito. Voce define o intervalo entre mensagens da sua campanha, do seu jeito.",
  },
];

export function Home() {
  useSeo({
    title: "Disparo de WhatsApp em massa pra empresas",
    description:
      "Plataforma de campanhas em massa no WhatsApp - API Web (QR Code) e API Oficial da Meta - e SMS. Meta Business Partner. Comece grátis.",
    path: "/",
  });

  return (
    <>
      <section className="mx-auto max-w-6xl px-5 pb-16 pt-14 md:pt-20">
        <div className="grid items-center gap-12 md:grid-cols-2">
          <div>
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border-2 border-ink bg-white px-3 py-1 text-xs font-bold">
              <BadgeCheck size={14} className="text-brand-600" /> Meta Business Partner
            </div>
            <h1 className="text-4xl font-extrabold leading-[1.1] md:text-5xl">
            Envie campanha de WhatsApp para <span className="text-brand-600">milhares</span> de
              clientes, sem virar bagunça!
            </h1>
            <p className="mt-5 max-w-[52ch] text-lg text-ink/70">
              Simplifique seus disparos em massa pelo WhatsApp e SMS.<br /> Conecte seu número via QR Code ou use a API oficial da Meta, importe sua planilha, agende seus envios e acompanhe tudo com relatórios completos, sem complicação e com dados de verdade.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <WhatsAppCta message="Ola! Quero comecar a usar a SendCube.">
                Falar com um especialista
              </WhatsAppCta>
              <Link
                to="/planos"
                className="inline-flex items-center rounded-lg border-2 border-ink bg-white px-6 py-3 text-sm font-bold shadow-solid-sm transition hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-solid"
              >
                Ver planos
              </Link>
            </div>
          </div>

          <div className="relative rounded-2xl border-2 border-ink bg-white p-6 shadow-solid">
            <p className="mb-4 text-xs font-bold uppercase tracking-wide text-ink/40">
              Campanha "Black Friday" · WhatsApp Oficial
            </p>
            <div className="space-y-3">
              <div className="flex items-center justify-between rounded-lg bg-paper px-4 py-3">
                <span className="text-sm font-medium">Enviados</span>
                <span className="text-sm font-bold">4.812</span>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-paper px-4 py-3">
                <span className="text-sm font-medium">Entregues</span>
                <span className="text-sm font-bold">4.756</span>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-paper px-4 py-3">
                <span className="text-sm font-medium">Lidos</span>
                <span className="text-sm font-bold text-brand-600">3.203</span>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-red-50 px-4 py-3">
                <span className="text-sm font-medium text-red-700">Falharam</span>
                <span className="text-sm font-bold text-red-700">56</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y-2 border-ink bg-white py-14">
        <div className="mx-auto max-w-6xl px-5">
          <h2 className="text-2xl font-bold">Você escolhe e a SendCube envia.</h2>
          <p className="mt-2 max-w-[60ch] text-ink/60">
            A SendCube oferece duas opções de envio, dentro da mesma plataforma, de acordo com a necessidade da sua operação.
            
          </p>

          <div className="mt-8 grid gap-6 md:grid-cols-2">
            <Link
              to="/whatsapp-web"
              className="group rounded-2xl border-2 border-ink bg-paper p-6 shadow-solid-sm transition hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-solid"
            >
              <QrCode size={28} className="text-brand-600" />
              <h3 className="mt-4 text-xl font-bold">API Web (QR Code)</h3>
              <p className="mt-2 text-sm text-ink/60">
                Conexão semelhante ao WhatsApp Web: você escolhe o número que deseja conectar e escanea um QR Code.
                Rápido pra começar, sem depender da aprovação da Meta.
              </p>
              <span className="mt-4 inline-block text-sm font-bold text-brand-600 group-hover:underline">
                Entender essa opção →
              </span>
            </Link>

            <Link
              to="/whatsapp-oficial"
              className="group rounded-2xl border-2 border-ink bg-paper p-6 shadow-solid-sm transition hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-solid"
            >
              <MessagesSquare size={28} className="text-brand-600" />
              <h3 className="mt-4 text-xl font-bold">API Oficial da Meta</h3>
              <p className="mt-2 text-sm text-ink/60">
                Integração direta com a Meta, com templates aprovados e número verificado -
                pensada para quem precisa de volume e estabilidade.
              </p>
              <span className="mt-4 inline-block text-sm font-bold text-brand-600 group-hover:underline">
                Entender essa opção →
              </span>
            </Link>
          </div>

          <Link to="/sms" className="mt-6 flex items-center gap-2 text-sm text-ink/50 hover:text-ink hover:underline">
            <Smartphone size={16} /> SMS também faz parte da plataforma (em breve).
          </Link>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-16">
        <h2 className="text-2xl font-bold">O que realmente muda no dia a dia</h2>
        <div className="mt-8 grid gap-6 sm:grid-cols-2">
          {FEATURES.map(({ icon: Icon, title, text }) => (
            <div key={title} className="rounded-2xl border-2 border-ink bg-white p-6 shadow-solid-sm">
              <Icon size={24} className="text-brand-600" />
              <h3 className="mt-3 font-bold">{title}</h3>
              <p className="mt-2 text-sm text-ink/60">{text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t-2 border-ink bg-ink py-16 text-white">
        <div className="mx-auto flex max-w-6xl flex-col items-start gap-6 px-5 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-2xl font-bold">Pronto para organizar suas campanhas?</h2>
            <p className="mt-2 max-w-[50ch] text-white/70">
              Dúvidas entre API Web e API
              Oficial? Clique aqui que ajudamos a escolher a melhor opção.
            </p>
          </div>
          <WhatsAppCta message="Ola! Quero comecar a usar a SendCube.">Falar no WhatsApp</WhatsAppCta>
        </div>
      </section>
    </>
  );
}
